/**
 * Seleção da implementação de armazenamento e reconciliação com o banco.
 */
import { db } from '@/db/schema';
import { updateState } from '@/db/repositories/localSignState';
import { IndexedDbBlobFileStore } from '@/storage/IndexedDbBlobFileStore';
import { OpfsFileStore } from '@/storage/OpfsFileStore';
import type { LocalMediaLookup } from '@/services/media/types';
import type { LocalMediaWriter } from '@/services/media/manualImport';
import type { OfflineFileStore } from '@/storage/types';

let instance: OfflineFileStore | null = null;

/**
 * Devolve o armazenamento de arquivos ativo (F10-T01, F10-T09).
 *
 * Prefere OPFS; cai para Blob no IndexedDB quando ele não existe. A escolha é feita
 * uma vez e mantida — alternar entre as duas no meio da sessão deixaria arquivos
 * inacessíveis em uma delas.
 */
export function getFileStore(): OfflineFileStore {
  if (instance) return instance;

  const opfs = new OpfsFileStore();
  instance = opfs.isSupported() ? opfs : new IndexedDbBlobFileStore();

  if (instance.id !== 'opfs') {
    console.info('[storage] OPFS indisponível; usando Blob no IndexedDB como fallback.');
  }

  return instance;
}

/** Apenas para testes. */
export function __setFileStoreForTests(store: OfflineFileStore | null): void {
  instance = store;
}

/** Adaptador para o resolvedor de mídia. */
export function mediaLookup(store: OfflineFileStore = getFileStore()): LocalMediaLookup {
  return {
    has: (signId) => store.exists(signId),
    read: async (signId) => {
      try {
        return await store.read(signId);
      } catch {
        return null;
      }
    },
  };
}

/** Adaptador para a importação manual. */
export function mediaWriter(store: OfflineFileStore = getFileStore()): LocalMediaWriter {
  return {
    write: (signId, blob) => store.write(signId, blob),
    remove: (signId) => store.remove(signId),
    has: (signId) => store.exists(signId),
  };
}

export interface ReconciliationReport {
  /** Banco diz que existe, filesystem não tem. Corrigidos para `none`. */
  missingFiles: string[];
  /** Arquivo existe, mas nenhum sinal do catálogo o referencia. */
  orphanFiles: string[];
  /** Arquivo existe e o banco não sabia. Corrigidos para `available`. */
  untrackedFiles: string[];
  /** Temporários descartados. */
  tempCleared: number;
  checkedAt: string;
}

/**
 * Reconcilia banco e filesystem (F10-T12).
 *
 * Existe porque as duas fontes divergem de verdade: o iOS pode evictar o
 * armazenamento de uma PWA sem persistência concedida e deixar o banco afirmando
 * que 300 vídeos estão disponíveis. Sem esta checagem, o usuário só descobriria no
 * avião — que é exatamente o cenário que o projeto existe para evitar.
 *
 * A correção é conservadora: ajusta o status offline e apaga temporários, mas
 * **não remove** arquivos órfãos automaticamente — eles podem ser de um sinal que
 * saiu do catálogo e vai voltar (PLAN §41).
 */
export async function reconcileStorage(
  store: OfflineFileStore = getFileStore(),
): Promise<ReconciliationReport> {
  const report: ReconciliationReport = {
    missingFiles: [],
    orphanFiles: [],
    untrackedFiles: [],
    tempCleared: 0,
    checkedAt: new Date().toISOString(),
  };

  report.tempCleared = await store.clearTemp().catch(() => 0);

  const filesOnDisk = new Set(await store.list());
  const states = await db.localSignState.toArray();
  const knownSignIds = new Set(await db.signs.toCollection().primaryKeys());

  for (const state of states) {
    const claimsAvailable = state.offline.status === 'available';
    const onDisk = filesOnDisk.has(state.signId);

    if (claimsAvailable && !onDisk) {
      report.missingFiles.push(state.signId);
      await updateState(state.signId, (current) => ({
        ...current,
        offline: {
          status: 'none',
          error: 'O arquivo local não foi encontrado e precisa ser baixado de novo.',
        },
      }));
      continue;
    }

    if (!claimsAvailable && onDisk) {
      // Arquivo existe e o banco não sabia: um download que concluiu logo antes de
      // a aplicação ser encerrada. Aproveitar é melhor que rebaixar.
      const size = await store.getSize(state.signId);
      report.untrackedFiles.push(state.signId);
      await updateState(state.signId, (current) => ({
        ...current,
        offline: {
          ...current.offline,
          status: 'available',
          ...(size !== null ? { totalBytes: size, bytesDownloaded: size } : {}),
        },
      }));
    }
  }

  const trackedSignIds = new Set(states.map((state) => state.signId));
  for (const signId of filesOnDisk) {
    if (trackedSignIds.has(signId)) continue;

    if (!knownSignIds.has(signId)) {
      report.orphanFiles.push(signId);
      continue;
    }

    // Arquivo de um sinal conhecido, mas sem estado registrado.
    const size = await store.getSize(signId);
    report.untrackedFiles.push(signId);
    await updateState(signId, (current) => ({
      ...current,
      offline: {
        ...current.offline,
        status: 'available',
        ...(size !== null ? { totalBytes: size, bytesDownloaded: size } : {}),
      },
    }));
  }

  return report;
}

export { OpfsFileStore } from '@/storage/OpfsFileStore';
export { IndexedDbBlobFileStore } from '@/storage/IndexedDbBlobFileStore';
export * from '@/storage/types';
